// Pieces Today and Appointments share: the status words of the prototype (FIXTURE §3: Booked, Arrived, In consultation,
// Completed, No-show), the chip tones, the type colour classes of the calendar (`.appt.exam/.fit/.cl/.dl`), the next front-desk
// action per status (BR-APPOINTMENTS-002), the warning chips (BR-APPOINTMENTS-015), the Bookings reference in words
// (BR-APPOINTMENTS-004, -009), the branch-local time helpers, and what a status action's answer shows and reads again.
import { type AppointmentBookingsRef, type AppointmentStatus, type AppointmentTransitionResult, type AppointmentView, type BookingsPushState, type CalendarView, type TodayView } from "@visionx/contracts";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import type { ApiResult } from "../api/client";
import type { TransitionSegment } from "../api/endpoints.appointments";
import { formatDate, formatTime } from "../lib/format";
import { Alert, Chip, type Tone } from "../ui/bits";

export const STATUS_WORD: Record<AppointmentStatus, string> = {
  Scheduled: "Booked", Confirmed: "Confirmed", Arrived: "Arrived", InProgress: "In consultation", Completed: "Completed", Cancelled: "Cancelled", NoShow: "No-show", Rescheduled: "Rescheduled",
};
const STATUS_TONE: Record<AppointmentStatus, { tone: Tone; dot: boolean }> = {
  Scheduled: { tone: "neutral", dot: false }, Confirmed: { tone: "neutral", dot: true }, Arrived: { tone: "info", dot: true }, InProgress: { tone: "accent", dot: true },
  Completed: { tone: "ok", dot: true }, Cancelled: { tone: "neutral", dot: false }, NoShow: { tone: "crit", dot: true }, Rescheduled: { tone: "neutral", dot: false },
};
export function ApptStatusChip({ status }: { status: AppointmentStatus }) {
  const t = STATUS_TONE[status];
  return <Chip tone={t.tone} dot={t.dot}>{STATUS_WORD[status]}</Chip>;
}

/** The transition routes and the button words of the front desk; `cancel` takes a reason and is offered separately. */
export const TRANSITION_OF: Record<Exclude<AppointmentStatus, "Scheduled" | "Rescheduled">, { segment: TransitionSegment | "cancel"; label: string }> = {
  Confirmed: { segment: "confirm", label: "Confirm" }, Arrived: { segment: "arrive", label: "Arrived" }, InProgress: { segment: "start", label: "Start" },
  Completed: { segment: "complete", label: "Complete" }, NoShow: { segment: "no-show", label: "No-show" }, Cancelled: { segment: "cancel", label: "Cancel" },
};
/** The one next step Today offers on a row: check in, start, complete. */
export function nextAction(a: AppointmentView): { segment: TransitionSegment; label: string } | null {
  if (a.status === "Scheduled" || a.status === "Confirmed") return a.allowedTransitions.includes("Arrived") ? { segment: "arrive", label: "Arrived" } : null;
  if (a.status === "Arrived") return a.allowedTransitions.includes("InProgress") ? { segment: "start", label: "Start" } : null;
  if (a.status === "InProgress") return a.allowedTransitions.includes("Completed") ? { segment: "complete", label: "Complete" } : null;
  return null;
}

/**
 * Where a slot is booked is the answer's own `bookingsUrl` (the appointment's, or the calendar's for the branch): a branch with
 * no mapped workspace has none, and then no Bookings link is offered anywhere (UC-APPOINTMENTS-007 A3) — never a generic fallback.
 */
const PUSH_WORD: Record<BookingsPushState, string> = { not_required: "nothing to push", pending: "push pending", synced: "push synced", failed: "push failed" };
/** The Source line of the detail panel: the push state, failures, a flagged push, a refused Bookings change, a superseded booking. */
export function bookingsRefText(b: AppointmentBookingsRef): string {
  const parts = [`${PUSH_WORD[b.pushState]}${b.pushFailures > 0 ? ` (${b.pushFailures} failed)` : ""}`];
  if (b.pushFlagged) parts.push("flagged for staff");
  const c = b.conflict;
  if (c) {
    if (c.kind === "status") parts.push(`conflict: Bookings says ${c.remoteStatus ?? "another status"}`);
    else if (c.kind === "reschedule") parts.push(`conflict: Bookings moved it${c.startAt ? ` to ${formatTime(c.startAt)}` : ""}`);
    else if (c.kind === "end_time_mismatch") parts.push(`conflict: Bookings end time ${c.endTime ?? "differs"}`);
    else parts.push("conflict: Bookings changed the staff member");
  }
  if (!b.current) parts.push("rescheduled — the booking moved to the new appointment");
  return parts.join(" · ");
}

/** The calendar block colour by type code (design/base.css): eye tests accent, fittings info, contact lenses warn, driver's licence grey. */
export function typeClass(code: string): "exam" | "fit" | "cl" | "dl" {
  if (code === "contact_lenses") return "cl";
  if (code === "drivers_licence_screening") return "dl";
  if (code === "new_glasses" || code === "prescription_sunglasses" || code === "fitting_collection") return "fit";
  return "exam";
}
export const TYPE_TONE: Record<ReturnType<typeof typeClass>, Tone> = { exam: "accent", fit: "info", cl: "warn", dl: "neutral" };

/** `MOK001/1 · dependant, 9` — the account code with the dependant marker of the prototype. */
export function patientSub(a: AppointmentView): string {
  return `${a.patient.accountCode}${a.patient.isDependant ? ` · dependant, ${a.patient.age}` : ""}`;
}
export const initialsOfName = (first: string, last: string): string => `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
/** Initials from the display name the appointments API sends (`Thandiwe Mokoena` → `TM`). */
export const initialsOfDisplay = (name: string): string => { const t = name.trim().split(/\s+/); return initialsOfName(t[0] ?? "", t.length > 1 ? t[t.length - 1]! : ""); };

export function ApptWarnings({ warnings }: { warnings: AppointmentView["warnings"] }) {
  if (warnings.length === 0) return null;
  return (
    <span className="chips">
      {warnings.map((w) => <Chip key={w.code} tone={w.code === "patient_dormant" ? "warn" : "crit"} dot>{w.message}</Chip>)}
    </span>
  );
}

/** Minutes past midnight of an instant in the organisation's timezone (through Intl, as formatTime is). */
export function minutesOfDay(iso: string): number {
  const [h, m] = formatTime(iso).split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}
export const hm = (minutes: number): string => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/** Phone width (design/screens/today-mobile.html): the lists replace the tables. jsdom has no matchMedia; then it is the desktop layout. */
export function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 720px)").matches : false));
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(max-width: 720px)");
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

/** Below this width the calendar's side column goes under the grid (UC-APPOINTMENTS-007 A6); `app.css` uses the same 1000 px. */
export const STACKED_QUERY = "(max-width: 999px)";

/**
 * The answer of a status action shown at once (UC-APPOINTMENTS-009 step 3): the appointment it carries replaces its row in every
 * calendar and Today read held, so the panel and the row show the new status and actions before the day is read again — which,
 * for the figures, the caller still does. A read that does not hold the appointment is left as it is. What else the action
 * changed is read again: the patient's record — its recall due date after a completion, its Appointments tab — even one read
 * seconds ago, which would otherwise be shown as it was for the fifteen seconds a read stays fresh; and the due recalls, on
 * Recalls and in Today's Needs attention, whose Booked column a cancelled or missed eye test changes.
 */
export function showAnswer(qc: QueryClient, v: AppointmentView): void {
  const swap = <T extends { appointments: AppointmentView[] }>(old: ApiResult<T> | undefined): ApiResult<T> | undefined =>
    old && old.data.appointments.some((a) => a.id === v.id) ? { ...old, data: { ...old.data, appointments: old.data.appointments.map((a) => (a.id === v.id ? v : a)) } } : old;
  qc.setQueriesData<ApiResult<CalendarView>>({ queryKey: ["calendar"] }, swap);
  qc.setQueriesData<ApiResult<TodayView>>({ queryKey: ["today"] }, swap);
  void qc.invalidateQueries({ queryKey: ["patient", v.patientId] });
  void qc.invalidateQueries({ queryKey: ["recalls"] });
  void qc.invalidateQueries({ queryKey: ["attention", "recalls"] });
}

/**
 * UC-APPOINTMENTS-010 step 2: the recall due date a completion set (BR-APPOINTMENTS-027), said as one plain sentence in a calm
 * notice — placed after any warning of the same answer; null for an action that set none (A4, A6).
 */
export function recallNotice(r: Pick<AppointmentTransitionResult, "patient" | "recallDueDate">): ReactNode {
  if (!r.recallDueDate) return null;
  return <Alert tone="info">{`${r.patient.displayName} is due for a recall on ${formatDate(r.recallDueDate)}.`}</Alert>;
}
